import { Button, Icon } from '../primitives';
import { useMountSound } from '../hooks/useMountSound';
import { useMountVibrate } from '../hooks/useMountVibrate';
import './semantic.groupC.css';

/**
 * Foundation semantic: FailOverlay（2026-06-12 R1: 体験文法の統一）
 *
 * 失敗の「瞬間結果＋二次アクション」オーバーレイ。T01 の .t01-fail-overlay
 * （apps/player/src/style.css）を手本に semantic 化し、19 テンプレでバラバラ
 * だった「失敗 4 流儀」を 1 つに統一する。
 *
 * Visual Grammar:
 *  - 失敗フィードバック＝中央 + shake（出現時に transform のみの keyframes で揺れる）。
 *  - 暗幕は error を color-mix した overlay（全 mood で「失敗の赤み」を帯びる）。
 *
 * 構成（中央縦並び）:
 *  - アイコンバッジ（lucide 'X'。`'X' in icons` を node で実在検証済み・lessons の落とし穴対策）
 *  - タイトル（--mg-error）→ サブタイトル → ボタン縦並び
 *  - やり直す = Button(secondary) / 未整理のまま進む = Button(ghost)
 *
 * 表示制御:
 *  - 表示/非表示は呼び出し側が制御する（出すなら描画・閉じるなら描画しない）。
 *  - 親ステージに position（relative 等）が必要（JudgementBurst と同じ前提）。
 *
 * 規約:
 *  - hex 直書き禁止。色は mood トークン経由（semantic.groupC.css 側）。
 *  - アイコンは Icon primitive 経由（直接 lucide-react を呼ばない）。
 */
export interface FailOverlayProps {
  /** タイトル。未指定時は「届かなかった……」（三点リーダは全角2字1組・guide §4.6） */
  title?: string;
  /** サブタイトル（任意）。1〜2 行程度を想定 */
  subtitle?: string;
  /** やり直しボタンのラベル。未指定時は「やり直す」 */
  retryLabel?: string;
  /** 進行ボタンのラベル。未指定時は「未整理のまま進む」 */
  proceedLabel?: string;
  /** やり直しを許可するか（既定 true）。false なら onRetry があってもボタンを出さない */
  allowRetry?: boolean;
  /** やり直し押下時のコールバック。未指定なら「やり直す」ボタンは出ない */
  onRetry?: () => void;
  /** 失敗のまま進む押下時のコールバック（必須） */
  onProceed: () => void;
  /** 追加 className（z-index 調整等） */
  className?: string;
}

export function FailOverlay({
  title = '届かなかった……',
  subtitle,
  retryLabel = 'やり直す',
  proceedLabel = '未整理のまま進む',
  allowRetry = true,
  onRetry,
  onProceed,
  className,
}: FailOverlayProps) {
  // R2 Wave2B: 失敗オーバーレイが表示された瞬間に fail 音（mount 時 1 回・mood は data-mood 追従）。
  // FailOverlay は「出す＝描画する」前提なので、マウント自体が「失敗が起きた瞬間」と一致する。
  useMountSound('fail');
  // R2 Wave3B: 同じ瞬間に fail 振動を添える（対応端末のみ・音の隣・音は不変）。
  useMountVibrate('fail');

  const classes = ['mg-failoverlay', className].filter(Boolean).join(' ');
  // 「やり直す」は allowRetry と onRetry の両方が揃った時のみ表示する
  const showRetry = allowRetry && typeof onRetry === 'function';

  return (
    <div className={classes} role="alertdialog" aria-modal="true" aria-label={title}>
      <div className="mg-failoverlay__content">
        <div className="mg-failoverlay__badge" aria-hidden>
          <Icon name="X" size={36} />
        </div>
        <h2 className="mg-failoverlay__title">{title}</h2>
        {subtitle ? <p className="mg-failoverlay__subtitle">{subtitle}</p> : null}
        <div className="mg-failoverlay__actions">
          {showRetry ? (
            <Button variant="secondary" size="md" onClick={onRetry}>
              {retryLabel}
            </Button>
          ) : null}
          <Button variant="ghost" size="md" onClick={onProceed}>
            {proceedLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
